import React, { createContext, useCallback, useContext, useRef, useState } from 'react';
import { AlertTriangle, Trash2, Info, HelpCircle } from 'lucide-react';
import { Modal } from './Modal';
import { Button } from './Button';
import { IconShape } from './IconShape';

/**
 * Hệ thống xác nhận dùng chung — thay window.confirm.
 *
 * Cách dùng:
 *   const confirm = useConfirm();
 *   const ok = await confirm({ title, message, variant:'danger', confirmText:'Xoá' });
 *   if (ok) { ... }
 *
 * Bọc <ConfirmProvider> quanh app (đã gắn ở App.js).
 */
const ConfirmContext = createContext(null);

const VARIANT_META = {
  danger: { icon: Trash2, iconVariant: 'danger', btn: 'danger' },
  warning: { icon: AlertTriangle, iconVariant: 'warning', btn: 'warning' },
  primary: { icon: HelpCircle, iconVariant: 'brand', btn: 'primary' },
  info: { icon: Info, iconVariant: 'brand', btn: 'primary' },
};

export function ConfirmProvider({ children }) {
  const [state, setState] = useState({ open: false, opts: {} });
  const resolverRef = useRef(null);

  const confirm = useCallback((opts = {}) => {
    return new Promise((resolve) => {
      resolverRef.current = resolve;
      setState({ open: true, opts });
    });
  }, []);

  const settle = useCallback((value) => {
    setState((s) => ({ ...s, open: false }));
    if (resolverRef.current) {
      resolverRef.current(value);
      resolverRef.current = null;
    }
  }, []);

  const opts = state.opts || {};
  const meta = VARIANT_META[opts.variant] || VARIANT_META.primary;
  const Icon = opts.icon || meta.icon;

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      <Modal
        open={state.open}
        onClose={() => settle(false)}
        size="sm"
        footer={
          <>
            <Button variant="secondary" onClick={() => settle(false)}>
              {opts.cancelText || 'Hủy'}
            </Button>
            <Button variant={meta.btn} onClick={() => settle(true)} autoFocus>
              {opts.confirmText || 'Xác nhận'}
            </Button>
          </>
        }
      >
        <div className="flex flex-col items-center text-center px-2 py-2">
          <IconShape icon={Icon} variant={meta.iconVariant} size="lg" className="mb-4" />
          {opts.title ? <h3 className="text-lg font-semibold text-heading">{opts.title}</h3> : null}
          {opts.message ? (
            <div className="mt-1.5 text-sm text-body leading-relaxed whitespace-pre-line">{opts.message}</div>
          ) : null}
        </div>
      </Modal>
    </ConfirmContext.Provider>
  );
}

export function useConfirm() {
  const ctx = useContext(ConfirmContext);
  if (!ctx) {
    // Fallback an toàn nếu Provider chưa gắn (không crash app).
    return async (opts = {}) =>
      typeof window !== 'undefined' && typeof window.confirm === 'function'
        ? window.confirm(opts.message || opts.title || 'Bạn có chắc chắn?')
        : true;
  }
  return ctx;
}

export default ConfirmProvider;
